<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { accountId } = useAccount();

const MAX_PROMPT = 8000;

const isLoading = ref(true);
const isSaving = ref(false);
const prompt = ref('');
const defaultPrompt = ref('');
// null → no custom prompt saved; the service uses the stock default.
const savedCustomPrompt = ref(null);

const baseUrl = computed(
  () => `/api/v1/accounts/${accountId.value}/mh_summarize`
);

const isUsingDefault = computed(() => !savedCustomPrompt.value);
const isDirty = computed(
  () => prompt.value !== (savedCustomPrompt.value || defaultPrompt.value)
);

const hydrate = data => {
  defaultPrompt.value = data.default_prompt || '';
  savedCustomPrompt.value = data.prompt || null;
  prompt.value = savedCustomPrompt.value || defaultPrompt.value;
};

const loadConfig = async () => {
  isLoading.value = true;
  try {
    const { data } = await window.axios.get(baseUrl.value);
    hydrate(data);
  } catch (error) {
    useAlert(t('AI_SUMMARIZE.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const persist = async value => {
  isSaving.value = true;
  try {
    const { data } = await window.axios.put(baseUrl.value, { prompt: value });
    hydrate(data);
    useAlert(t('AI_SUMMARIZE.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('AI_SUMMARIZE.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

// Saving text identical to the stock default clears the override instead of
// storing a redundant copy — the page then keeps tracking future defaults.
const save = () =>
  persist(
    prompt.value.trim() === defaultPrompt.value.trim() ? '' : prompt.value
  );
const resetToDefault = () => persist('');

onMounted(loadConfig);
</script>

<template>
  <div class="flex flex-col w-full max-w-3xl ltr:mr-auto rtl:ml-auto">
    <BaseSettingsHeader
      :title="$t('AI_SUMMARIZE.TITLE')"
      :description="$t('AI_SUMMARIZE.DESCRIPTION')"
    />
    <SectionLayout
      :title="$t('AI_SUMMARIZE.PROMPT.TITLE')"
      :description="$t('AI_SUMMARIZE.PROMPT.DESCRIPTION')"
    >
      <div v-if="isLoading" class="text-sm text-n-slate-11">
        {{ $t('AI_SUMMARIZE.LOADING') }}
      </div>
      <div v-else class="flex flex-col gap-3">
        <span
          class="text-xs"
          :class="isUsingDefault ? 'text-n-slate-10' : 'text-n-violet-9'"
        >
          {{
            isUsingDefault
              ? $t('AI_SUMMARIZE.PROMPT.USING_DEFAULT')
              : $t('AI_SUMMARIZE.PROMPT.USING_CUSTOM')
          }}
        </span>
        <textarea
          v-model="prompt"
          rows="18"
          :maxlength="MAX_PROMPT"
          :placeholder="$t('AI_SUMMARIZE.PROMPT.PLACEHOLDER')"
          class="!mb-0 text-sm font-mono"
        />
        <span class="text-xs text-n-slate-10 ltr:text-right rtl:text-left">
          {{ `${prompt.length} / ${MAX_PROMPT}` }}
        </span>
        <div class="flex gap-2">
          <NextButton
            :label="$t('AI_SUMMARIZE.SAVE')"
            :is-loading="isSaving"
            :disabled="isSaving || !isDirty || !prompt.trim()"
            sm
            @click="save"
          />
          <NextButton
            :label="$t('AI_SUMMARIZE.RESET')"
            :disabled="isSaving || (isUsingDefault && !isDirty)"
            slate
            faded
            sm
            @click="resetToDefault"
          />
        </div>
      </div>
    </SectionLayout>
  </div>
</template>
